import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import AudioPanel from '@/components/production/AudioPanel';
import ClipsGallery from '@/components/production/ClipsGallery';
import DesignReferences from '@/components/production/DesignReferences';
import FinalOutput from '@/components/production/FinalOutput';
import KeyframeGallery from '@/components/production/KeyframeGallery';
import PaperCutCard from '@/components/production/PaperCutCard';
import ScriptBreakdownCard from '@/components/production/ScriptBreakdownCard';
import ShotListCard from '@/components/production/ShotListCard';
import SourceFootage from '@/components/production/SourceFootage';
import type { ProductionState } from '@/hooks/useProduction';

export interface ProductionStep {
  key: string;
  label: string;
  /** Short state line under the rail label — the thing tabs never told the user. */
  status: string;
  /** `done` = artifact exists; `active` = being produced now; `pending` = not reached. */
  state: 'done' | 'active' | 'pending';
  /** Utility steps (Asset Browser, Shot Editor) sit below the pipeline divider. */
  utility?: boolean;
  node: ReactNode;
}

/**
 * Builds the ordered production path shown in the rail.
 *
 * The order IS the pipeline: break down / source → plan → design → storyboard →
 * shoot → audio → deliver. Generation-only steps are hidden for footage productions,
 * which have neither a storyboard nor designed sets.
 *
 * Each step reports its own status from state that already exists, so the rail
 * communicates progress at a glance instead of greying out an empty tab.
 */
export function useProductionSteps(
  state: ProductionState,
  productionId: string | undefined,
  extras: { shotEditor?: ReactNode; assetBrowser?: ReactNode },
): ProductionStep[] {
  const { t } = useTranslation('production');
  const { shotEditor, assetBrowser } = extras;

  return useMemo(() => {
    const isFootage = state.productionType === 'footage';
    const running = state.status === 'running';
    const assets = state.assets || [];
    const count = (...types: string[]) => assets.filter((a) => types.includes(a.type) && !a.path.includes('/rejected/')).length;

    const plannedShots = (state.shotPlan as { shots?: unknown[] } | null)?.shots?.length ?? 0;
    // The phase the engine says it's working on right now, used to mark the rail.
    const phase = state.phaseTiming?.step ?? '';

    /** Derive a step's state + status line from "how many of how many". */
    const progress = (
      made: number,
      total: number,
      unit: string,
      activePhase?: boolean,
    ): Pick<ProductionStep, 'state' | 'status'> => {
      if (total > 0 && made >= total) {
        return { state: 'done', status: t('step.status.completeWith', { summary: `${total} ${unit}` }) };
      }
      if (made > 0 || (running && activePhase)) {
        return {
          state: 'active',
          status:
            total > 0
              ? t('step.status.inProgress', { done: made, total, unit })
              : t('step.status.inProgressCount', { summary: `${made} ${unit}` }),
        };
      }
      return {
        state: 'pending',
        status: total > 0 ? t('step.status.queued', { total, unit }) : t('step.status.notStarted'),
      };
    };

    const steps: ProductionStep[] = [];

    if (isFootage) {
      const n = count('footage');
      steps.push({
        key: 'source',
        label: t('step.source'),
        state: n > 0 ? 'done' : 'pending',
        status: n > 0 ? t('step.status.clipsIngested', { count: n }) : t('step.status.awaitingIngest'),
        node: <SourceFootage assets={state.assets} productionId={productionId} />,
      });
      steps.push({
        key: 'papercut',
        label: t('step.papercut'),
        state: 'pending',
        status: t('step.status.storyBeforePictures'),
        node: productionId ? <PaperCutCard productionId={productionId} /> : null,
      });
    } else {
      steps.push({
        key: 'breakdown',
        label: t('step.breakdown'),
        state: state.scriptBreakdown ? 'done' : 'pending',
        status: state.scriptBreakdown ? t('step.status.complete') : t('step.status.notStarted'),
        node: <ScriptBreakdownCard breakdown={state.scriptBreakdown} />,
      });
    }

    steps.push({
      key: 'shotlist',
      label: t('step.shotlist'),
      state: plannedShots > 0 ? 'done' : 'pending',
      status: plannedShots > 0 ? t('step.status.shotsPlanned', { count: plannedShots }) : t('step.status.awaitingBreakdown'),
      node: <ShotListCard shotPlan={state.shotPlan} />,
    });

    if (!isFootage) {
      const chars = count('character');
      const locs = count('location');
      steps.push({
        key: 'design',
        label: t('step.design'),
        state: chars + locs > 0 ? 'done' : 'pending',
        status:
          chars + locs > 0
            ? t('step.status.designSummary', {
                characters: t('design.charactersCount', { count: chars }),
                locations: t('design.locationsCount', { count: locs }),
              })
            : t('step.status.notDesigned'),
        node: <DesignReferences breakdown={state.scriptBreakdown} />,
      });
      steps.push({
        key: 'storyboard',
        label: t('step.storyboard'),
        ...progress(count('keyframe'), plannedShots, t('unit.frames'), phase === 'storyboard'),
        node: <KeyframeGallery />,
      });
    }

    const dailiesStep: ProductionStep = {
      key: 'dailies',
      label: t('step.dailies'),
      ...progress(count('clip'), plannedShots, t('unit.clips'), phase === 'shooting'),
      node: <ClipsGallery />,
    };
    const audioCount = count('dialog', 'vo', 'vo_booth', 'music', 'sfx', 'audio');
    const audioStep: ProductionStep = {
      key: 'audio',
      label: t('step.audio'),
      state: audioCount > 0 ? 'done' : 'pending',
      status:
        audioCount > 0
          ? t('step.status.tracks', { count: audioCount })
          : t(isFootage ? 'step.status.audioAfterDailies' : 'step.status.audioAfterStoryboard'),
      node: <AudioPanel />,
    };
    // Follow the pipeline: a generative production records voice and music BEFORE
    // the shoot (voices are reviewed before clips are generated), so Audio precedes
    // Dailies. Footage carries its own sound; its dailies come first.
    steps.push(...(isFootage ? [dailiesStep, audioStep] : [audioStep, dailiesStep]));

    const hasFinal = (state.finalAssets?.size ?? 0) > 0 || count('final') > 0 || !!state.finalVideo;
    steps.push({
      key: 'final',
      label: t('step.final'),
      state: state.finalVideo ? 'done' : hasFinal ? 'active' : 'pending',
      status: state.finalVideo ? t('step.status.readyToDownload') : t('step.status.pendingRender'),
      node: <FinalOutput state={state} />,
    });

    if (shotEditor) {
      steps.push({
        key: 'editor',
        label: t('step.editor'),
        state: 'done',
        status: t('step.status.reorder'),
        utility: true,
        node: shotEditor,
      });
    }
    if (assetBrowser) {
      steps.push({
        key: 'assets',
        label: t('step.assets'),
        state: 'done',
        status: t('step.status.everything'),
        utility: true,
        node: assetBrowser,
      });
    }

    return steps;
  }, [state, productionId, shotEditor, assetBrowser, t]);
}
